"use client";

import { useState, useEffect } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { TimeSelect } from "@/components/ui/time-select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  Users,
  ChevronLeft,
  Clock,
  Building2,
  Globe,
  Plus,
  Route,
  Check,
  DollarSign,
  ShoppingCart,
  Minus,
  Calendar,
  User,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { addDays } from "date-fns";
import { cn } from "@/lib/utils";
import { BookingCalendar } from "@/components/booking/BookingCalendar";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { CUSTOMER_TYPES } from "@/lib/constants/customer-types";
import { BOOKING_TYPE_IDS } from "@/lib/constants/booking-types";
import { SLOT_STATUSES } from "@/lib/constants/slot-statuses";
import { CapacityWarningDialog } from "@/components/booking/booking-new/CapacityWarningDialog";
import { ROLES } from "@/lib/constants/roles";
import { PRICE_TYPE_IDS } from "@/lib/constants/price-types";
import { useStaffCalendarAvailability } from "@/hooks/useStaffCalendarAvailability";
import { PricingService } from "@/lib/pricing/pricing-service-unified";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { useBookingPixelTracking } from "@/lib/hooks/useFacebookPixel";

// Import extracted components
import { CartSidebar } from "@/components/booking/booking-new/cart/CartSidebar";
import { TimeSlotGrid } from "@/components/booking/booking-new/time-slots/TimeSlotGrid";
import { CollapsibleSection } from "@/components/booking/booking-new/ui/CollapsibleSection";
import {
  findAvailableStudio,
  checkForOverlappingShows,
  parseDateParam,
} from "@/lib/booking/booking-new/helpers";
import { NewRoadShowModal } from "@/components/booking/modals/NewRoadShowModal";
import { BookingGapWarningModal } from "@/components/booking/BookingGapWarningModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

// Import type definitions from extracted files
import type { Show, Studio, AddOn } from "@/types";
import type {
  CustomerType,
  TimeSlotPricing,
  NewBookingClientProps,
  BookingCart,
  SelectedShowStudio,
} from "@/types/booking-new.types";

// Define types for API response data
interface StudioShowHostBooking {
  id: number;
  studio_id: number;
  show_session_timeslot: string;
  show_time_slot_pricing_id: number;
  is_archived: boolean;
  studios?: {
    id: number;
    name: string;
    capacity: number;
  };
  show_time_slot_pricing?: {
    show_id: number;
    shows?: {
      id: number;
      duration_minutes: number;
      min_participants: number;
      max_participants: number;
      show_infos?: Array<{ name: string; desc?: string; lang: string }>;
    };
  };
}

interface BookingAddOn {
  id: number;
  add_on_id: number;
  quantity: number;
  amount: number;
  total_price: number;
  is_archived: boolean;
  add_ons?: {
    id: number;
    pricing_id: number;
    add_ons_info?: Array<{ name: string; desc?: string; lang: string }>;
  };
}

// CollapsibleSection component has been extracted to a separate file
// TimeSlotGrid component has been extracted to a separate file
// CartSidebar component has been extracted to a separate file

// The old TimeSlotGrid implementation has been removed (was lines 82-1807)
// The duplicate CartSidebar function has been removed (was lines 1812-2628)

export default function NewBookingClient({
  venueId,
  venueName,
  venueCurrency,
  venueDefaultLanguage,
  availableLanguages = [],
  shows,
  studios,
  addOns,
  customerTypes,
  timeSlotPricing,
  debugInfo,
}: NewBookingClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isSuperAdmin = pathname?.includes("/superadmin/") || false;
  const bookingId = searchParams?.get("bookingId");
  const isRoadShow = searchParams?.get("type") === "roadshow";
  const isAddonMode = searchParams?.get("addon") === "yes";

  const [isLoadingBooking, setIsLoadingBooking] = useState(false);
  const [isLoadingBookingData, setIsLoadingBookingData] = useState(false);
  const [priceSetFromBookingData, setPriceSetFromBookingData] = useState(false);
  const [isSavingBooking, setIsSavingBooking] = useState(false);
  const [capacityWarning, setCapacityWarning] = useState<{
    open: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    message: "",
    onConfirm: () => {},
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isRoadShowModalOpen, setIsRoadShowModalOpen] = useState(false);
  const [groupSize, setGroupSize] = useState(6);
  const [startTime, setStartTime] = useState("11:00");
  const [endTime, setEndTime] = useState("22:00");

  // State for controlling CollapsibleSection expanded states
  // When addon=yes in URL, expand add-ons and collapse time slots
  const [expandedSections, setExpandedSections] = useState({
    timeSlots: !isAddonMode,
    addOns: isAddonMode,
  });

  // Initialize with date from URL if provided, otherwise today's date
  const getInitialDate = () => {
    const paramDate = parseDateParam(searchParams?.get("date"));
    if (paramDate) return paramDate;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const [date, setDateInternal] = useState<Date | undefined>(getInitialDate());

  const setDate = (newDate: Date | undefined) => {
    setDateInternal(newDate);

    // Remove date from URL when user changes date via the date picker
    // This prevents confusion where URL shows one date but UI shows another
    const currentParams = new URLSearchParams(searchParams?.toString() || '');
    if (currentParams.has('date')) {
      currentParams.delete('date');
      const newUrl = currentParams.toString()
        ? `${pathname}?${currentParams.toString()}`
        : pathname;
      router.replace(newUrl, { scroll: false });
    }
  };
  const [venueDetails, setVenueDetails] = useState<{
    min_participant: number;
    max_participant: number;
  } | null>(null);

  const [bookingCart, setBookingCart] = useState<BookingCart>({
    shows: [],
    groupSize: 6,
    customerType: null,
    addOns: [],
    notes: "",
    bookingType: CUSTOMER_TYPES.CORPORATE as "corporate",
    date: undefined,
    // No preset — staff must actively pick the booking language.
    lang: "",
    kitId: null,
    roadShowBasePrice: null,
    roadShowPerPersonPrice: null,
  });

  // Previous booking language, captured when editing an existing booking, so
  // the update request can report old_lang -> lang (the PUT route logs the
  // change and notifies operations only when it actually differs). Undefined
  // when creating a new booking.
  const [originalLang, setOriginalLang] = useState<string | undefined>(
    undefined,
  );

  // Add booking price state to main component
  const [bookingPrice, setBookingPrice] = useState(500);
  const [priceMode, setPriceMode] = useState<"per_person" | "fixed">(
    "per_person",
  );
  const [isPriceManuallyOverridden, setIsPriceManuallyOverridden] =
    useState(false);

  // Add state for add-on price overrides
  const [addonPriceOverrides, setAddonPriceOverrides] = useState<
    Record<number, number>
  >({});

  // Add state for price modifiers
  const [activePriceModifier, setActivePriceModifier] = useState<any>(null);
  const [originalPrice, setOriginalPrice] = useState<number>(0);

  // Add state for existing booking pricing (from database)
  const [existingBookingPricing, setExistingBookingPricing] = useState<{
    base_amount_excl_vat: number; // Total base amount (shows + add-ons excl VAT)
    vat_amount: number; // Total VAT amount
    vat_rate: number; // VAT rate percentage
    total_price: number; // Grand total (incl VAT)
    base_price_excluding_vat: number; // Show price only (excl add-ons, excl VAT)
  } | null>(null);

  // Add refresh trigger for time slot grid
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Track what user actually changed (for change tracking in API)
  const [changedFields, setChangedFields] = useState({
    isParticipantChange: false,
    isAddOnChange: false,
    isPriceChange: false,
    isShowChange: false,
    isNotesChange: false,
  });

  // Add state for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSlotSelection, setPendingSlotSelection] = useState<{
    show: Show;
    studio: Studio;
    time: string;
    price: number | null;
  } | null>(null);
  const [proceedWithSlotSelectionFn, setProceedWithSlotSelectionFn] = useState<
    (() => void) | null
  >(null);

  // Gap warning modal state
  const [gapWarningOpen, setGapWarningOpen] = useState(false);
  const [gapWarningData, setGapWarningData] = useState<{
    showName: string;
    date: string;
    startTime: string;
    endTime: string;
    timeGap: number;
  } | null>(null);

  // Initialize the staff calendar availability hook
  const {
    getDateAvailability: getRealDateAvailability,
    isLoading: isLoadingAvailability,
  } = useStaffCalendarAvailability(
    venueId,
    bookingCart.bookingType === "corporate" ? "2" : "1", // Customer type ID
    bookingCart.groupSize,
    startTime,
    endTime,
  );

  // Initialize date after component mounts to avoid hydration issues
  useEffect(() => {
    // Only set default date if not editing a booking
    if (!bookingId && !date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setDate(today);
      // Reset the price protection flag for new bookings
      setPriceSetFromBookingData(false);
    }
  }, [bookingId]);

  // Remove incompatible add-ons when booking type changes
  useEffect(() => {
    const customerTypeId = bookingCart.bookingType === "corporate" ? 2 : 1;
    const isRoadShow = bookingCart.bookingType === "roadshow";
    // An add-on is compatible when its road-show scope matches the booking and,
    // for on-site bookings, its customer type matches (or is unset).
    const isCompatible = (addOn: any) => {
      const onRoad = (addOn as any).is_on_road ?? false;
      if (isRoadShow) return onRoad === true;
      return (
        onRoad !== true &&
        (addOn.customer_type_id === null ||
          addOn.customer_type_id === undefined ||
          addOn.customer_type_id === customerTypeId)
      );
    };
    const incompatibleAddOns = bookingCart.addOns.filter(
      (item) => !isCompatible(item.addOn),
    );

    if (incompatibleAddOns.length > 0) {
      setBookingCart((prev) => ({
        ...prev,
        addOns: prev.addOns.filter((item) => isCompatible(item.addOn)),
      }));
      toast.info(
        `Removed ${incompatibleAddOns.length} add-on${incompatibleAddOns.length > 1 ? "s" : ""} not compatible with ${bookingCart.bookingType} bookings`,
      );
    }
  }, [bookingCart.bookingType]);

  // Update booking price when shows in cart change
  // But don't interfere when loading booking data
  useEffect(() => {
    // Skip automatic price updates when loading existing booking data
    if (isLoadingBookingData) {
      return;
    }

    // Skip automatic price updates if price was set from booking data (editing mode)
    if (priceSetFromBookingData && bookingId) {
      return;
    }

    // Skip automatic price updates if price was manually overridden
    if (isPriceManuallyOverridden) {
      return;
    }

    if (bookingCart.shows.length > 0) {
      // In fixed mode, the price should remain constant regardless of number of shows
      // In per_person mode, use the highest price among all shows
      if (priceMode === "fixed") {
        // For fixed mode, only update price if it hasn't been set yet
        // The price should be set once from the first show and remain constant
        if (bookingPrice === 500 || bookingPrice === 0) {
          const firstShowPrice = bookingCart.shows[0].price;
          if (firstShowPrice !== undefined && firstShowPrice !== null) {
            setBookingPrice(firstShowPrice);
          }
        }
        // Otherwise, keep the existing price (don't change it when shows are added/removed)
      } else {
        // For per_person mode, use the highest price among all shows
        // For private bookings when editing, prices are stored excl. VAT and need VAT added
        const isPrivateCustomer = bookingCart.bookingType === CUSTOMER_TYPES.PRIVATE;

        const highestPrice = Math.max(
          ...bookingCart.shows.map((showItem) => {
            if (showItem.price === undefined || showItem.price === null) {
              throw new Error(`No price set for show ${showItem.show?.id || 'unknown'}`);
            }
            let price = showItem.price;

            // If this is a private booking and we're editing an existing booking,
            // the price stored in showItem might be excl. VAT, so we need to add VAT
            if (isPrivateCustomer && bookingId && showItem.vat_percentage) {
              // The price from the booking is excl. VAT, add VAT for display
              price = price * (1 + showItem.vat_percentage / 100);
            }

            return price;
          }),
        );
        setBookingPrice(highestPrice);
      }
      setIsPriceManuallyOverridden(false); // Reset flag when auto-calculated
    } else {
      // Reset to default price when cart is empty
      setBookingPrice(500);
      setIsPriceManuallyOverridden(false);
    }
  }, [
    bookingCart.shows,
    isLoadingBookingData,
    priceSetFromBookingData,
    bookingId,
    priceMode,
    isPriceManuallyOverridden,
    bookingPrice,
  ]);

  // Fetch venue details for min/max participants
  useEffect(() => {
    const fetchVenueDetails = async () => {
      try {
        const response = await fetch(`/api/venues/${venueId}/details`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setVenueDetails({
              min_participant: data.data.min_participant || 3,
              max_participant: data.data.max_participant || 50,
            });
            // Update group size to be within the venue's limits
            setGroupSize((prev) => {
              const min = data.data.min_participant || 3;
              const max = data.data.max_participant || 50;
              return Math.max(min, Math.min(prev, max));
            });
          }
        }
      } catch (error) {}
    };

    fetchVenueDetails();
  }, [venueId]);

  // Fetch booking data if bookingId is present
  useEffect(() => {
    if (!bookingId) return;

    // Wait for shows and studios to be loaded
    if (shows.length === 0 || studios.length === 0) {
      return;
    }

    const fetchBookingData = async () => {
      setIsLoadingBooking(true);
      setIsLoadingBookingData(true);
      try {
        // For edit page, include archived items (shows history of changes)
        const apiUrl = `/api/staff/venues/${venueId}/bookings/${bookingId}?includeArchived=true`;

        const response = await fetch(apiUrl);
        if (!response.ok) {
          let errorMessage = "Failed to fetch booking";
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            // Response is not JSON (likely HTML error page)
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        let data;
        try {
          data = await response.json();
        } catch (e) {
          console.error("Failed to parse response as JSON:", e);
          throw new Error("Invalid response format from server");
        }
        if (data.success && data.data) {
          const booking = data.data;

          // Store existing booking pricing from database
          if (
            booking.base_amount_excl_vat !== undefined &&
            booking.vat_amount !== undefined
          ) {
            setExistingBookingPricing({
              base_amount_excl_vat:
                parseFloat(booking.base_amount_excl_vat) || 0,
              vat_amount: parseFloat(booking.vat_amount) || 0,
              vat_rate: parseFloat(booking.vat_rate) || 0,
              total_price: parseFloat(booking.total_price) || 0,
              base_price_excluding_vat:
                parseFloat(booking.base_price_excluding_vat) || 0,
            });
          }

          // Debug all pricing-related fields
          // Extract date from the first show_session_timeslot
          // Note: For multi-show bookings, we assume all shows are on the same date
          const sessionDate =
            booking.studio_show_host_bookings?.[0]?.show_session_timeslot;
          if (sessionDate) {
            // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
            const datePart = sessionDate.split("T")[0];
            const [year, month, day] = datePart.split("-").map(Number);
            const bookingDate = new Date(year, month - 1, day); // month is 0-indexed
            bookingDate.setHours(0, 0, 0, 0);
            setDate(bookingDate);
          } else {
            // Set today's date as fallback
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            setDate(today);
          }

          // Set group size
          setGroupSize(booking.participant_count || 6);

          // Set booking type. Road shows are identified by the studio booking's
          // booking_type_id (ON_ROAD); otherwise fall back to the customer type.
          const isRoadShowBooking =
            booking.studio_show_host_bookings?.some(
              (sb: any) => sb.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD,
            );
          const bookingType = isRoadShowBooking
            ? "roadshow"
            : booking.customer_type_id === 2
              ? "corporate"
              : "private";

          // Determine price mode and calculate unit price
          // Try multiple sources for the total booking price
          let totalBookingPrice = 0;

          // First check if booking has total_price or amount directly (staff API response)
          if (booking.total_price) {
            totalBookingPrice = booking.total_price;
          } else if (booking.amount && booking.participant_count) {
            // If we have per-person amount, calculate total
            totalBookingPrice = booking.amount * booking.participant_count;
          } else if (booking.assigned_pricing?.price) {
            // Try assigned_pricing (from foreign key relationship)
            totalBookingPrice = booking.assigned_pricing.price;
          } else if (
            Array.isArray(booking.assigned_pricing) &&
            booking.assigned_pricing[0]?.price
          ) {
            totalBookingPrice = booking.assigned_pricing[0].price;
          } else if (booking.pricing?.price) {
            totalBookingPrice = booking.pricing.price;
          } else if (
            Array.isArray(booking.pricing) &&
            booking.pricing[0]?.price
          ) {
            totalBookingPrice = booking.pricing[0].price;
          }

          // If no direct booking pricing, try to get from time slot pricing
          if (totalBookingPrice === 0) {
            const timeSlotPrice =
              booking.studio_show_host_bookings?.[0]?.time_slot_pricing
                ?.price_point?.pricing?.price ||
              booking.studio_show_host_bookings?.[0]?.time_slot_pricing
                ?.price_point?.pricing?.[0]?.price;

            if (timeSlotPrice > 0) {
              // This might be per-person price, so calculate total
              totalBookingPrice =
                timeSlotPrice * (booking.participant_count || 6);
            }
          }
          const participantCount = booking.participant_count || 6;

          // For now, assume per-person pricing if total price is divisible by participant count
          // This is a heuristic - you might need to adjust based on actual business logic
          let determinedPriceMode: "per_person" | "fixed" = "per_person";
          let unitPrice = 0; // Initialize to 0, only fallback to 500 if absolutely no price found

          // Check price mode based on database fields
          // Priority: use per_person_mode_price_excluding_vat for per-person bookings
          if (booking.price_type_id === 2 && booking.per_person_mode_price_excluding_vat) {
            // Per-person price mode - use the correct field
            determinedPriceMode = "per_person";
            unitPrice = parseFloat(booking.per_person_mode_price_excluding_vat);
          } else if (
            booking.fixed_mode_price_excluding_vat !== undefined &&
            booking.fixed_mode_price_excluding_vat !== null &&
            parseFloat(booking.fixed_mode_price_excluding_vat) > 0
          ) {
            // Fixed price mode
            determinedPriceMode = "fixed";
            unitPrice = parseFloat(booking.fixed_mode_price_excluding_vat);
          } else if (
            booking.per_person_mode_price_excluding_vat !== undefined &&
            booking.per_person_mode_price_excluding_vat !== null &&
            parseFloat(booking.per_person_mode_price_excluding_vat) > 0
          ) {
            // Per-person price mode
            determinedPriceMode = "per_person";
            unitPrice = parseFloat(booking.per_person_mode_price_excluding_vat);
          } else if (booking.amount && booking.amount > 0) {
            // Use the amount field which contains the per-person price
            unitPrice = parseFloat(booking.amount);
            // Determine mode based on price_type_id using constants
            determinedPriceMode = booking.price_type_id === PRICE_TYPE_IDS.FIXED ? "fixed" : "per_person";
          } else if (
            totalBookingPrice &&
            participantCount &&
            totalBookingPrice > 0
          ) {
            // Don't calculate unit price by dividing total by participants
            // This breaks for bookings with minimum charge
            // Instead, trust the stored per_person_mode_price_excluding_vat
            // Fall back to calculation only if really needed
            determinedPriceMode = "per_person";
            unitPrice = 0; // Will be set from time slot pricing
          } else {
            // Fallback: try to get per-person price directly from time slot pricing
            const directPerPersonPrice =
              booking.studio_show_host_bookings?.[0]?.time_slot_pricing
                ?.price_point?.pricing?.price ||
              booking.studio_show_host_bookings?.[0]?.time_slot_pricing
                ?.price_point?.pricing?.[0]?.price;

            if (directPerPersonPrice && directPerPersonPrice > 0) {
              determinedPriceMode = "per_person";
              unitPrice = directPerPersonPrice;
            } else {
              // Last resort: try to find price information from the show pricing data passed as props
              // This might be available from the time slot pricing props
              // Don't set a default price here - let the slot pricing mechanism handle it
            }
          }

          setPriceMode(determinedPriceMode);

          // Only set the booking price if we found a valid price from the booking data
          // Otherwise, let the time slot pricing mechanism handle it
          if (unitPrice > 0) {
            let displayPrice = unitPrice;

            if (bookingType === "private") {
              // For private customers, add VAT to the per-person price
              // unitPrice already contains the correct per-person price without VAT
              const vatRate = booking.vat_rate ? parseFloat(booking.vat_rate) : 25;
              displayPrice = unitPrice * (1 + vatRate / 100);
              displayPrice = Math.round(displayPrice * 100) / 100;
            }
            // For corporate customers, displayPrice remains as unitPrice (excl VAT)

            setBookingPrice(displayPrice);
            setPriceSetFromBookingData(true);
          } else {
            // Don't set the priceSetFromBookingData flag, allowing automatic price updates
          }

          // Load price modifier if exists (check both property names)
          const priceModifier =
            booking.price_modifier || booking.price_modifiers;
          if (priceModifier) {
            setActivePriceModifier(priceModifier);
            // The original price should be the calculated unit price before modifiers
            // For per-person pricing, it's unitPrice * participant count
            // For fixed pricing, it's just unitPrice
            const baseTotal =
              determinedPriceMode === "per_person"
                ? unitPrice * participantCount
                : unitPrice;
            setOriginalPrice(baseTotal);
          }

          // Preload the saved language so the cart selector reflects the
          // booking's actual language when editing. If the booking somehow has
          // no language, leave it unset so staff must actively choose.
          const loadedLang = booking.lang || "";
          setOriginalLang(loadedLang);

          setBookingCart((prev) => ({
            ...prev,
            groupSize: booking.participant_count || 6,
            bookingType: bookingType,
            notes: booking.special_request || "",
            lang: loadedLang,
            // Restore road-show kit + pricing so they're prefilled when editing.
            ...(isRoadShowBooking && {
              kitId:
                booking.studio_show_host_bookings?.find(
                  (sb: any) => sb.kit_id != null,
                )?.kit_id ?? null,
              roadShowBasePrice:
                booking.show_base_price != null
                  ? Number(booking.show_base_price)
                  : null,
              roadShowPerPersonPrice:
                booking.per_person_mode_price_excluding_vat != null
                  ? Number(booking.per_person_mode_price_excluding_vat)
                  : null,
            }),
          }));

          // Find and set the shows (filter out archived shows)
          const bookingShows: BookingCart["shows"] = [];
          if (
            booking.studio_show_host_bookings &&
            booking.studio_show_host_bookings.length > 0
          ) {
            // Filter out archived shows
            const activeShows = booking.studio_show_host_bookings.filter(
              (show: StudioShowHostBooking) => !show.is_archived,
            );

            for (const studioBooking of activeShows) {
              // Get show ID from nested structure
              const showId =
                studioBooking.show_time_slot_pricing?.show_id ||
                studioBooking.show_time_slot_pricing?.shows?.id ||
                studioBooking.time_slot_pricing?.show?.id ||
                studioBooking.time_slot_pricing?.show_id ||
                studioBooking.show_id;

              // Get show data from local shows array only (which has complete data including show_studios)
              const show = shows.find((s) => s.id === showId);

              if (show) {
                const studioId = studioBooking.studio_id;
                // Get studio from API response or local studios array
                let studio = null;
                if (studioBooking.studios) {
                  // Use studio data from API response
                  studio = {
                    id: studioBooking.studios.id,
                    name: studioBooking.studios.name,
                    capacity: studioBooking.studios.capacity,
                  };
                } else {
                  // Fallback to local studios array
                  studio = studios.find((s) => s.id === studioId);
                }

                if (studio) {
                  const sessionDate = new Date(
                    studioBooking.show_session_timeslot,
                  );
                  // Use the calculated unit price, but if it's 0, try to get from time slot pricing
                  let showPrice = unitPrice;

                  if (unitPrice === 0) {
                    // Try to get price from time slot pricing
                    const timeSlotPrice =
                      studioBooking.show_time_slot_pricing?.price_point?.pricing
                        ?.price ||
                      studioBooking.show_time_slot_pricing?.price_point
                        ?.pricing?.[0]?.price ||
                      studioBooking.time_slot_pricing?.price_point?.pricing
                        ?.price;
                    if (timeSlotPrice > 0) {
                      showPrice = timeSlotPrice;
                    }
                  }

                  const timeStr = sessionDate.toTimeString().slice(0, 5);
                  const [hour, minute] = timeStr.split(":").map(Number);
                  const startMinutes = hour * 60 + minute;

                  // Use custom duration if available, otherwise use show duration
                  const durationMinutes = studioBooking.custom_duration_minutes || show.duration_minutes;
                  if (!durationMinutes) {
                    throw new Error(`No duration specified for show ${show.id}`);
                  }
                  const endMinutes = startMinutes + durationMinutes;
                  const endTimeStr = `${Math.floor(endMinutes / 60)
                    .toString()
                    .padStart(
                      2,
                      "0",
                    )}:${(endMinutes % 60).toString().padStart(2, "0")}`;

                  // Get VAT percentage from pricing data or booking
                  const vatPercentage =
                    studioBooking.show_time_slot_pricing?.price_point?.pricing
                      ?.vat_percentage ||
                    studioBooking.show_time_slot_pricing?.price_point?.pricings
                      ?.vat_percentage ||
                    booking.vat_rate ||
                    20; // Default to 20% if not found

                  const showData = {
                    show,
                    studio,
                    date: (() => {
                      // Format date as YYYY-MM-DD without timezone issues
                      const year = sessionDate.getFullYear();
                      const month = String(sessionDate.getMonth() + 1).padStart(
                        2,
                        "0",
                      );
                      const day = String(sessionDate.getDate()).padStart(
                        2,
                        "0",
                      );
                      return `${year}-${month}-${day}`;
                    })(),
                    time: timeStr,
                    duration_minutes: show.duration_minutes,
                    price: showPrice,
                    vat_percentage: vatPercentage, // Add VAT percentage to show data
                    startTime: timeStr,
                    endTime: endTimeStr,
                    // Add customEndTime if there's a custom duration
                    ...(studioBooking.custom_duration_minutes ? { customEndTime: endTimeStr } : {}),
                    showTimeSlotPricingId:
                      studioBooking.show_time_slot_pricing_id, // Preserve the original pricing ID
                  };
                  bookingShows.push(showData);
                }
              }
            }

            if (bookingShows.length > 0) {
              // Add shows to cart but keep the isLoadingBookingData flag active
              // until after the cart is updated to prevent price interference
              setBookingCart((prev) => ({
                ...prev,
                shows: bookingShows,
              }));
              // Trigger a refresh of the TimeSlotGrid
              setRefreshTrigger((prev) => prev + 1);
            }
          }

          // Set add-ons with historical prices from database (filter out archived add-ons)
          if (booking.booking_add_ons && booking.booking_add_ons.length > 0) {
            // Filter out archived add-ons
            const activeAddOns = booking.booking_add_ons.filter(
              (addon: BookingAddOn) => !addon.is_archived,
            );

            const cartAddOns = activeAddOns
              .map((ba: any) => {
                const addOn = addOns.find((a) => a.id === ba.add_on_id);
                if (addOn) {
                  // Use historical price from booking_add_ons.amount, but fall back to
                  // master add-on price if amount is 0 (can happen when add-on was added
                  // via customer portal which previously didn't set the amount field)
                  const historicalPrice = parseFloat(ba.amount) || addOn.price;
                  return {
                    addOn: {
                      ...addOn,
                      price: historicalPrice,
                    },
                    quantity: ba.quantity || 1,
                  };
                }
                return null;
              })
              .filter(Boolean);

            setBookingCart((prev) => ({
              ...prev,
              addOns: cartAddOns,
            }));

            // Also initialize addonPriceOverrides with historical prices (only for active add-ons)
            // Fall back to master price if amount is 0
            const priceOverrides: Record<number, number> = {};
            activeAddOns.forEach((ba: any) => {
              const addOn = addOns.find((a: any) => a.id === ba.add_on_id);
              priceOverrides[ba.add_on_id] = parseFloat(ba.amount) || addOn?.price || 0;
            });
            setAddonPriceOverrides(priceOverrides);
          }

          // Note: Customer information should be loaded in the CartSidebar component
          // when it opens, as it manages its own customerInfo state

          toast.success("Booking data loaded successfully");
        } else {
          // Set today's date as fallback when booking is not found
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          setDate(today);
        }
      } catch (error) {
        if (error instanceof Error) {
          toast.error(`Failed to load booking: ${error.message}`);
        } else {
          toast.error("Failed to load booking data");
        }
        // Set today's date as fallback on error
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setDate(today);
      } finally {
        setIsLoadingBooking(false);
        setIsLoadingBookingData(false);
      }
    };

    fetchBookingData();
  }, [bookingId, shows, studios, addOns]);

  // Open cart automatically when editing a booking (but not when just adding addons)
  useEffect(() => {
    if (bookingId && !isAddonMode) {
      // Delay to ensure booking data is loaded and UI is ready
      const timer = setTimeout(() => {
        setIsCartOpen(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [bookingId, isAddonMode]);

  // Listen for navigate to add-ons event from cart
  useEffect(() => {
    const handleNavigateToAddons = () => {
      // Collapse time slots section and expand add-ons section
      setExpandedSections({
        timeSlots: false,
        addOns: true,
      });

      // Scroll to add-ons section after a small delay to allow sections to update
      setTimeout(() => {
        const addOnsSection = document.querySelector('[data-section="addons"]');
        if (addOnsSection) {
          addOnsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    };

    window.addEventListener('navigateToAddons', handleNavigateToAddons);
    return () => {
      window.removeEventListener('navigateToAddons', handleNavigateToAddons);
    };
  }, []);

  // Scroll to add-ons section when page loads with addon=yes
  useEffect(() => {
    if (isAddonMode) {
      // Wait for page to fully load and sections to render
      setTimeout(() => {
        const addOnsSection = document.querySelector('[data-section="addons"]');
        if (addOnsSection) {
          addOnsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [isAddonMode]);

  // Don't render anything until hydration is complete
  // Only show loading screen when actually loading booking data
  if (bookingId && isLoadingBooking) {
    return (
      <section className="min-h-screen bg-background">
        <div className="bg-background border-b border-border/40 sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold">Staff Booking System</h2>
                <span className="text-sm text-muted-foreground">
                  • {bookingId ? "Edit booking" : "New booking"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-center py-8">
            <div className="text-muted-foreground">
              {isLoadingBooking ? "Loading booking data..." : "Loading..."}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Wrapper for imported checkForOverlappingShows that provides local context
  const checkForOverlappingShowsLocal = (
    newShow: Show,
    newTime: string,
    newStudioId: number,
  ): {
    hasOverlap: boolean;
    overlappingShow?: any;
    availableStudios?: Studio[];
  } => {
    return checkForOverlappingShows(
      newShow,
      newTime,
      newStudioId,
      bookingCart.shows,
      studios,
      groupSize,
      date,
    );
  };

  // Legacy implementation - to be removed
  const checkForOverlappingShowsOLD = (
    newShow: Show,
    newTime: string,
    newStudioId: number,
  ): {
    hasOverlap: boolean;
    overlappingShow?: any;
    availableStudios?: Studio[];
  } => {
    const [newHour, newMinute] = newTime.split(":").map(Number);
    const newStartMinutes = newHour * 60 + newMinute;
    if (!newShow.duration_minutes) {
      alert("Cannot add show: Duration not specified");
      return { hasOverlap: true, overlappingShow: null, availableStudios: [] };
    }
    const newEndMinutes = newStartMinutes + newShow.duration_minutes;

    // Check current date
    const currentDateStr = date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : "";

    // Track which studios are occupied during this time
    const occupiedStudios = new Set<number>();
    let overlappingShow: any = null;

    for (const existingShow of bookingCart.shows) {
      // Only check shows on the same date
      if (existingShow.date !== currentDateStr) continue;

      const [existingHour, existingMinute] = existingShow.time
        .split(":")
        .map(Number);
      const existingStartMinutes = existingHour * 60 + existingMinute;
      const existingEndMinutes =
        existingStartMinutes + (existingShow.show.duration_minutes || 0);
      if (!existingShow.show.duration_minutes) {
        console.error(`Warning: No duration for show ${existingShow.show.id}`);
      }

      // Check for time overlap
      const hasTimeOverlap =
        (newStartMinutes >= existingStartMinutes &&
          newStartMinutes < existingEndMinutes) ||
        (newEndMinutes > existingStartMinutes &&
          newEndMinutes <= existingEndMinutes) ||
        (newStartMinutes <= existingStartMinutes &&
          newEndMinutes >= existingEndMinutes);

      if (hasTimeOverlap) {
        occupiedStudios.add(existingShow.studio.id);

        // If this is the specific studio we're checking, record the overlap
        if (existingShow.studio.id === newStudioId) {
          overlappingShow = {
            show: existingShow.show,
            time: existingShow.time,
            studio: existingShow.studio,
            startTime: `${Math.floor(existingStartMinutes / 60)}:${String(existingStartMinutes % 60).padStart(2, "0")}`,
            endTime: `${Math.floor(existingEndMinutes / 60)}:${String(existingEndMinutes % 60).padStart(2, "0")}`,
          };
        }
      }
    }

    // Find available studios for this time slot
    const availableStudios = studios.filter(
      (studio) =>
        !occupiedStudios.has(studio.id) &&
        studio.capacity >= groupSize &&
        studio.status_id === STATUS_IDS.STUDIO_ACTIVE,
    );

    return {
      hasOverlap: !!overlappingShow,
      overlappingShow,
      availableStudios,
    };
  };

  const handleTimeSlotSelect = async (
    show: Show,
    originalStudio: Studio,
    time: string,
    price?: number | null,
    pricePointId?: number | null,
    userRoleId?: number | null,
    vatPercentage?: number | null,
    showTimeSlotPricingId?: number | null,
    availableStudiosFromApi?: Studio[] | null,
  ) => {
    let studio = originalStudio;

    const isRoadShowSelect =
      bookingCart.bookingType === "roadshow" ||
      (show as any).show_type === "on_road";

    // If we have available studios from API, prefer the smallest one that fits
    if (availableStudiosFromApi && availableStudiosFromApi.length > 0) {
      if (isRoadShowSelect) {
        // Road shows are capacity-agnostic (the show, not the studio, caps the
        // group), and the API already excluded booked/duration-busy studios — so
        // just take the first available studio instead of the clicked one (which
        // may be booked).
        studio = availableStudiosFromApi[0];
      } else {
        // Filter studios that have enough capacity
        const suitableStudios = availableStudiosFromApi.filter(
          s => s.capacity >= bookingCart.groupSize
        );

        if (suitableStudios.length > 0) {
          // Sort by capacity (ascending) to get the smallest studio that fits
          suitableStudios.sort((a, b) => a.capacity - b.capacity);

          // Use the smallest suitable studio
          studio = suitableStudios[0];
        }
      }
    } else {
      // Fallback to checking cart-only if no API data available
      const occupiedStudioIds = bookingCart.shows
        .filter(s => s.time === time)
        .map(s => s.studio.id);

      // Check if original studio is occupied in cart
      if (occupiedStudioIds.includes(originalStudio.id)) {
        // Find an alternative studio
        const showStudios = show.show_studios || [];
        const assignedStudioIds = showStudios.map(ss => ss.studio_id);
        const studiosForShow = assignedStudioIds.length > 0
          ? studios.filter(s => assignedStudioIds.includes(s.id))
          : studios;

        const availableStudio = findAvailableStudio(
          studiosForShow,
          bookingCart.groupSize,
          occupiedStudioIds
        );

        if (availableStudio) {
          studio = availableStudio;
        }
      }
    }

    const adjustedGroupSize = Math.max(
      show.min_participants,
      Math.min(groupSize, show.max_participants),
    );
    setGroupSize(adjustedGroupSize);

    // Update the booking price with the actual slot price, fallback to 500 if no price provided
    let slotPrice = price !== null && price !== undefined ? price : 500;
    let appliedModifier = null;

    // Debug the date being used - use timezone-safe formatting
    const selectedDate = date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : "";

    // Store original slot price before any modifications
    // This is the BASE price that will be stored in the cart
    // The pricing engine will apply modifiers during calculation
    const originalSlotPrice = slotPrice;

    // Skip price modifier auto-application when editing an existing booking
    // When editing, the price modifier is already loaded from the booking data
    if (!bookingId) {
      // Check for price modifiers for this show and date (NEW BOOKINGS ONLY)
      try {
        // We need to get the price point ID - this might be available from the show's pricing
        // For now, let's check all modifiers for the date range
        const response = await fetch(`/api/venues/${venueId}/price-modifiers`);
        if (response.ok) {
          const data = await response.json();
          const modifiers = data.priceModifiers || [];

          // Find modifier that applies to this date
          const applicableModifier = modifiers.find((modifier: any) => {
            const startDate = new Date(modifier.start_date);
            const endDate = new Date(modifier.end_date);
            const currentDate = new Date(selectedDate);

            // Check if booking date is within modifier date range
            const isWithinDateRange =
              currentDate >= startDate && currentDate <= endDate;

            if (!isWithinDateRange) return false;

            // Check if modifier applies to all or specific price points
            if (!modifier.price_points || modifier.price_points.length === 0) {
              return true;
            }

            // For price-point-specific modifiers, we need to get the price point ID
            // This is a bit complex since we need to match show + time + studio to get the pricing
            // For now, let's try to find it from the timeSlotPricing prop

            // Use the price point ID passed from TimeSlotGrid
            let showPricePointId = pricePointId;

            if (
              showPricePointId &&
              modifier.price_points.includes(showPricePointId)
            ) {
              return true;
            }

            return false;
          });

          if (applicableModifier) {
            // Store modifier for display/tracking purposes only
            // The backend pricing engine will apply the actual modifier during calculation
            appliedModifier = applicableModifier;
            setOriginalPrice(slotPrice);
            setActivePriceModifier(applicableModifier);

            // ✅ IMPORTANT: Do NOT manually apply modifiers here!
            // The cart stores the base price (originalSlotPrice) and the backend
            // pricing engine will apply the modifier during price calculation.
            // Manual application here would cause DOUBLE APPLICATION of modifiers.
            //
            // The modifier is stored in state for:
            // - Display purposes in the cart
            // - Sending to backend so it knows which modifier to apply
            // - But backend pricing engine does the actual calculation
          } else {
            setActivePriceModifier(null);
            setOriginalPrice(0);
          }
        }
      } catch (error) {}
    }
    // Check if this show is already in the cart at the same time
    const existingShowIndex = bookingCart.shows.findIndex(
      (s) =>
        s.show.id === show.id && s.date === selectedDate && s.time === time,
    );

    if (existingShowIndex >= 0) {
      // Show already exists, don't add duplicate
      toast.error("This show is already in your cart at the selected time");
      return;
    }

    // NOTE: TimeSlotGrid handles all conflict detection and studio selection
    // It pre-reassigns conflicting shows and selects the correct studio
    // Trust the studio passed from TimeSlotGrid - no need to re-check here

    // Add the new show to the array
    // If editing an existing booking, mark this as a show change
    if (bookingId) {
      setChangedFields((prev) => ({ ...prev, isShowChange: true }));
    }

    const [hour, minute] = time.split(":").map(Number);
    const startMinutes = hour * 60 + minute;
    if (!show.duration_minutes) {
      toast.error("Cannot add show: Duration not specified");
      return;
    }
    const endMinutes = startMinutes + show.duration_minutes;
    const endTime = `${Math.floor(endMinutes / 60)
      .toString()
      .padStart(2, "0")}:${(endMinutes % 60).toString().padStart(2, "0")}`;


    // Note: Gap check is performed in TimeSlotGrid.tsx (handleSlotClick)
    // This ensures consistency and avoids duplicate checks

    setBookingCart((prev) => ({
      ...prev,
      shows: [
        ...prev.shows,
        {
          show,
          studio: studio, // Studio will be automatically selected based on optimal capacity
          time,
          date: selectedDate,
          duration_minutes: show.duration_minutes,
          price: originalSlotPrice, // Always use base price - pricing engine will apply modifiers
          startTime: time,
          endTime: endTime,
          userRoleId: userRoleId,
          vat_percentage: vatPercentage, // Add VAT percentage to cart item
          showTimeSlotPricingId: showTimeSlotPricingId || undefined, // Convert null to undefined
          pricePointId: pricePointId || undefined, // Convert null to undefined
        },
      ],
      groupSize: adjustedGroupSize,
    }));

    toast.success(
      `${show.show_infos?.[0]?.name || `Show ${show.id}`} added to cart`,
    );
  };

  const handleGapWarningCancel = () => {
    setGapWarningOpen(false);
    setGapWarningData(null);
  };

  const handleAddOnToggle = (addOn: AddOn) => {
    // Mark add-on change flag when user explicitly adds/removes add-on
    setChangedFields((prev) => ({ ...prev, isAddOnChange: true }));

    setBookingCart((prev) => {
      const existingIndex = prev.addOns.findIndex(
        (item) => item.addOn.id === addOn.id,
      );

      if (existingIndex >= 0) {
        // Remove add-on
        return {
          ...prev,
          addOns: prev.addOns.filter((_, index) => index !== existingIndex),
        };
      } else {
        // Add add-on with correct initial quantity
        // For per-person add-ons, quantity should match group size
        const initialQuantity = addOn.is_per_person ? prev.groupSize : 1;
        return {
          ...prev,
          addOns: [...prev.addOns, { addOn, quantity: initialQuantity }],
        };
      }
    });
  };

  const handleQuantityChange = (addonId: number, quantity: number) => {
    // Mark add-on change flag when user explicitly changes quantity
    setChangedFields((prev) => ({ ...prev, isAddOnChange: true }));

    if (quantity <= 0) {
      setBookingCart((prev) => ({
        ...prev,
        addOns: prev.addOns.filter((item) => item.addOn.id !== addonId),
      }));
    } else {
      setBookingCart((prev) => ({
        ...prev,
        addOns: prev.addOns.map((item) =>
          item.addOn.id === addonId ? { ...item, quantity } : item,
        ),
      }));
    }
  };

  const handleRemoveAddon = (addonId: number) => {
    // Mark add-on change flag when user explicitly removes add-on
    setChangedFields((prev) => ({ ...prev, isAddOnChange: true }));

    setBookingCart((prev) => ({
      ...prev,
      addOns: prev.addOns.filter((item) => item.addOn.id !== addonId),
    }));
  };

  const handleRemoveShow = (index: number) => {
    // Mark show change flag when user explicitly removes a show
    setChangedFields((prev) => ({ ...prev, isShowChange: true }));

    // Check if this will be the last show removed
    const remainingShows = bookingCart.shows.filter((_, i) => i !== index);

    setBookingCart((prev) => ({
      ...prev,
      shows: prev.shows.filter((_, i) => i !== index),
    }));

    // If no shows left, clear the price modifier and close the cart sidebar
    if (remainingShows.length === 0) {
      // Clear price modifier when all shows are removed
      setActivePriceModifier(null);
      setOriginalPrice(0);
      setIsCartOpen(false);
      toast.success("Show removed - cart is now empty");
    } else {
      toast.success("Show removed from cart");
    }

    // Reset price protection flag to allow automatic price recalculation
    // when user manually removes shows and adds new ones with different prices
    setPriceSetFromBookingData(false);
  };

  const handleUpdateShowTime = (
    showIndex: number,
    startTime: string,
    endTime: string,
  ) => {
    // Mark show change flag when user explicitly changes show time
    setChangedFields((prev) => ({ ...prev, isShowChange: true }));

    setBookingCart((prev) => ({
      ...prev,
      shows: prev.shows.map((show, index) =>
        index === showIndex
          ? { ...show, time: startTime, customEndTime: endTime }
          : show,
      ),
    }));
  };

  const handleUpdateShowStudio = (
    showIndex: number,
    studioId: number,
  ) => {
    // Find the studio object from the studios array
    const selectedStudio = studios.find(s => s.id === studioId);
    if (!selectedStudio) {
      console.error("Studio not found:", studioId);
      return;
    }

    // Mark show change flag when user explicitly changes studio
    setChangedFields((prev) => ({ ...prev, isShowChange: true }));

    setBookingCart((prev) => ({
      ...prev,
      shows: prev.shows.map((show, index) =>
        index === showIndex
          ? { ...show, studio: selectedStudio }
          : show,
      ),
    }));
  };

  const handleUpdateAddonPrice = (addonId: number, price: number) => {
    // Store the modified add-on price in parent component state
    setAddonPriceOverrides((prev) => ({ ...prev, [addonId]: price }));
  };

  // Function to check for active price modifiers
  const checkPriceModifiers = async (
    showPricePointId: number,
    bookingDate: string,
  ) => {
    try {
      const response = await fetch(`/api/venues/${venueId}/price-modifiers`);
      if (response.ok) {
        const data = await response.json();
        const modifiers = data.priceModifiers || [];

        // Find modifier that applies to this date and price point
        const applicableModifier = modifiers.find((modifier: any) => {
          const startDate = new Date(modifier.start_date);
          const endDate = new Date(modifier.end_date);
          const currentDate = new Date(bookingDate);

          // Check if booking date is within modifier date range
          const isWithinDateRange =
            currentDate >= startDate && currentDate <= endDate;

          if (!isWithinDateRange) return false;

          // Check if modifier applies to this price point
          if (modifier.price_points && Array.isArray(modifier.price_points)) {
            return modifier.price_points.includes(showPricePointId);
          }

          // If no specific price points, applies to all
          return true;
        });

        if (applicableModifier) {
          setActivePriceModifier(applicableModifier);
          return applicableModifier;
        }
      }
    } catch (error) {}

    setActivePriceModifier(null);
    return null;
  };

  // Function to apply price modifier to a price
  const applyPriceModifier = (basePrice: number, modifier: any) => {
    if (!modifier) return basePrice;

    if (modifier.modifier_type === "discount") {
      return basePrice * (1 - modifier.modifier_value / 100);
    } else if (modifier.modifier_type === "surcharge") {
      return basePrice * (1 + modifier.modifier_value / 100);
    }

    return basePrice;
  };

  const handleUpdateGroupSize = (groupSize: number) => {
    // Mark participant change flag when user explicitly changes group size
    setChangedFields((prev) => ({ ...prev, isParticipantChange: true }));

    setBookingCart((prev) => ({
      ...prev,
      groupSize: groupSize,
      // Update per-person add-on quantities to match new group size
      // Note: This is automatic recalculation, NOT a user add-on change
      addOns: prev.addOns.map((item) =>
        item.addOn.is_per_person ? { ...item, quantity: groupSize } : item,
      ),
    }));
    // Also update the main group size state to keep them in sync
    setGroupSize(groupSize);
  };

  const handleDateChange = (newDate: Date) => {
    // Mark show change flag when user explicitly changes date in cart
    if (bookingId) {
      setChangedFields((prev) => ({ ...prev, isShowChange: true }));
    }

    // Clear price modifier when date changes
    // The modifier may not apply to the new date
    setActivePriceModifier(null);
    setOriginalPrice(0);

    // Update the date in the parent component
    setDate(newDate);

    // Update all shows in the cart to use the new date
    setBookingCart((prev) => ({
      ...prev,
      shows: prev.shows.map((show) => ({
        ...show,
        date: (() => {
          const year = newDate.getFullYear();
          const month = String(newDate.getMonth() + 1).padStart(2, "0");
          const day = String(newDate.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        })(),
      })),
    }));
  };

  const handleSaveBooking = async (
    isDraft: boolean,
    customerInfo: any,
    companyInfo: any,
    priceMode: "per_person" | "fixed",
    bookingPrice: number,
    bookingStatus?: number,
    partnerId?: number | null,
    endCustomerName?: string | null,
  ) => {
    try {
      setIsSavingBooking(true);

      if (bookingCart.shows.length === 0) {
        toast.error("Please select at least one show");
        setIsSavingBooking(false);
        return;
      }

      // Validate required fields
      if (!customerInfo.name || !customerInfo.email) {
        toast.error("Please enter customer name and email");
        setIsSavingBooking(false);
        return;
      }

      if (bookingCart.bookingType === "corporate" && !companyInfo.companyName) {
        toast.error("Please enter company name");
        setIsSavingBooking(false);
        return;
      }

      // Road shows require an equipment kit to be selected.
      if (bookingCart.bookingType === "roadshow" && bookingCart.kitId == null) {
        toast.error("Please select a kit for the road show");
        setIsSavingBooking(false);
        return;
      }

      // Language must be actively chosen — no venue-default preset.
      if (!bookingCart.lang) {
        toast.error("Please select a booking language");
        setIsSavingBooking(false);
        return;
      }

      // Phone is now optional for admin bookings
      // Remove the phone validation check

      // Note: Gap check already performed when adding shows to cart (TimeSlotGrid.tsx)
      // No need to check again during booking creation

      // Use the bookingPrice from cart sidebar (user can modify this)
      // This takes precedence over the original show prices
      const highestShowPrice = bookingPrice;

      // Prepare shows data for API
      const showsData: Array<{
        showId: number | undefined;
        studioId: number | undefined;
        assignedPricingId: number | null;
        showTimeSlotPricingId: number | null;
        pricePointId: number | null;
        showSessionTimeslot: string;
        date: string;
        time: string;
        dayOfWeek: string;
        price: number;
        userRoleId?: number | null;
        customDuration?: number;
        addOns?: any[];
      }> = [];

      // Collect all show data
      for (const showItem of bookingCart.shows) {
        try {
          // Get live pricing information from the API to ensure we have the correct IDs
          const dayOfWeek = (() => {
            // Parse date string as local date to avoid timezone issues
            const [year, month, day] = showItem.date.split("-").map(Number);
            return new Date(year, month - 1, day).getDay();
          })();
          let timeSlotPriceInfo = null;
          let pricingId: number | null = null;
          let showTimeSlotPricingId: number | null = null;
          let assignedPricingId: number | null = null;

          // If we're editing a booking and have the original pricing ID, use it as the primary source
          // BUT skip if it's the invalid fallback value of 1
          // However, we still need to fetch the other pricing IDs (assignedPricingId, pricingId)
          // So we should always call the API for multi-show bookings or when we don't have all IDs
          const hasValidStoredPricingId = showItem.showTimeSlotPricingId && showItem.showTimeSlotPricingId !== 1;

          // For now, always fetch fresh pricing data to ensure we have all required IDs
          // TODO: In the future, store all three IDs (showTimeSlotPricingId, assignedPricingId, pricingId) in the booking
          if (false) { // Temporarily disabled - always fetch fresh data
            showTimeSlotPricingId = showItem.showTimeSlotPricingId ?? null;
          } else {
            // Otherwise, call the shows API to get the most up-to-date pricing information
            const customerTypeId =
              bookingCart.bookingType === "corporate" ? 2 : 1;

            // Build query params
            const queryParams = new URLSearchParams({
              venue_id: venueId,
              timeslot_start: showItem.time + ":00",
              timeslot_end: showItem.time + ":00",
              day_of_week: dayOfWeek.toString(),
              customer_type_id: customerTypeId.toString(),
              date: showItem.date,
              lang: "en",
            });

            // For multi-show bookings, don't filter by participants at the API level
            // The participants will be distributed across shows
            if (bookingCart.shows.length === 1) {
              // Single show - apply participant filter
              queryParams.append('participants', bookingCart.groupSize.toString());
            }
            // For multi-show bookings, we need pricing regardless of individual capacity

            console.log('Fetching pricing for show:', {
              showName: showItem.show?.show_infos?.[0]?.name,
              showId: showItem.show?.id,
              time: showItem.time,
              participants: bookingCart.groupSize,
              isMultiShow: bookingCart.shows.length > 1,
              queryString: queryParams.toString()
            });

            const response = await fetch(
              `/api/staff/venues/${venueId}/shows?${queryParams}`,
            );

            console.log('API response status:', {
              ok: response.ok,
              status: response.status,
              statusText: response.statusText
            });

            if (response.ok) {
              const result = await response.json();
              console.log('API response for show', showItem.show?.id, ':', {
                success: result.success,
                dataLength: result.data?.length,
                data: result.data
              });

              if (result.success && result.data) {
                // Find the pricing data for the selected show and time
                const selectedShowPricing = result.data.find(
                  (item: any) =>
                    item.show_id === showItem.show?.id &&
                    item.time === showItem.time + ":00",
                );

                console.log('Selected pricing for show:', {
                  showId: showItem.show?.id,
                  found: !!selectedShowPricing,
                  pricing: selectedShowPricing
                });

                if (selectedShowPricing) {
                  // IMPORTANT: assigned_pricing_id should reference the pricings table
                  // Use the pricing_id from the API response which is the actual pricings.id
                  if (!selectedShowPricing.pricing_id && !showItem._useStoredPricing) {
                    throw new Error(`No pricing_id found for show ${showItem.show?.id} at time ${showItem.time}`);
                  }
                  assignedPricingId = selectedShowPricing.pricing_id || showItem.pricingId;
                  // show_time_slot_pricing_id should reference the show_time_slot_pricing table
                  showTimeSlotPricingId = selectedShowPricing.id || showItem.showTimeSlotPricingId;
                  // price_point_id references the show_price_points table
                  if (!selectedShowPricing.price_point_id && !showItem._useStoredPricing) {
                    throw new Error(`No price_point_id found for show ${showItem.show?.id} at time ${showItem.time}`);
                  }
                  pricingId = selectedShowPricing.price_point_id || showItem.pricePointId;
                } else if (
                  showItem.showTimeSlotPricingId ||
                  showItem.pricePointId ||
                  showItem.pricingId
                ) {
                  // The live shows API returned no pricing for this slot — most
                  // commonly because the price point was ARCHIVED (the shows route
                  // only returns ACTIVE price points), and possibly replaced with a
                  // new one. When editing an existing booking the slot still has
                  // valid stored pricing (archived rows persist, so the FKs stay
                  // valid), so fall back to it and keep the booking's old price
                  // instead of blocking the edit. This is independent of single-/
                  // multi-show — it's purely about the archived price point.
                  // Brand-new bookings (no stored pricing) still fall through to the
                  // capacity/availability checks below.
                  const archivedShowName =
                    showItem.show?.show_infos?.[0]?.name ||
                    `Show ${showItem.show?.id}`;
                  console.warn(
                    `No active pricing returned for ${archivedShowName} at ${showItem.time} ` +
                      `(price point likely archived); using the booking's stored pricing.`,
                  );
                  showItem._useStoredPricing = true;
                } else {
                  // Check if the show was filtered out due to participant constraints
                  const participantCount = bookingCart.groupSize;
                  const showMinParticipants = showItem.show?.min_participants || 0;
                  const showMaxParticipants = showItem.show?.max_participants || 999;

                  // Calculate total capacity of all shows in cart
                  const totalMaxCapacity = bookingCart.shows.reduce((sum, cartShow) => {
                    return sum + (cartShow.show?.max_participants || 0);
                  }, 0);

                  // For multi-show bookings, check if total participants can be distributed
                  if (bookingCart.shows.length > 1) {
                    if (participantCount > totalMaxCapacity) {
                      const showNames = bookingCart.shows.map(s =>
                        s.show?.show_infos?.[0]?.name || `Show ${s.show?.id}`
                      ).join(" + ");
                      throw new Error(
                        `Combined capacity of ${showNames} is ${totalMaxCapacity} participants (you have ${participantCount})`
                      );
                    }
                    // For multi-show bookings, participants will be distributed across shows
                    // So we should NOT check individual show capacity here
                    // Just log a warning and let the API handle distribution
                    console.log(`Multi-show booking: ${participantCount} participants will be distributed across shows`);
                    throw new Error(`No pricing data found for show ${showItem.show?.show_infos?.[0]?.name || showItem.show?.id} at time ${showItem.time}. The API may have filtered this show, but in multi-show bookings, participants should be distributed across shows.`);
                  } else if (participantCount < showMinParticipants || participantCount > showMaxParticipants) {
                    // Single show booking - show warning but don't throw error
                    const showName = showItem.show?.show_infos?.[0]?.name || "This show";
                    const warningMessage = `${showName} requires ${showMinParticipants}-${showMaxParticipants} participants (you have ${participantCount})`;

                    // Store warning to show after all data is collected
                    if (!showItem._capacityWarning) {
                      showItem._capacityWarning = warningMessage;
                      showItem._useStoredPricing = true; // Flag to skip pricing validation
                    }
                    console.warn(`Capacity warning: ${warningMessage}`);

                    // For capacity warnings, try to get pricing from the timeSlotPricing data
                    // This is the pricing that was shown in the grid when the user selected the slot
                    const fallbackPricing = timeSlotPricing.find(
                      (tp) =>
                        tp.show_id === showItem.show?.id &&
                        tp.time_slot.startsWith(showItem.time) &&
                        (tp.day_of_week === dayOfWeek || tp.day_of_week === null),
                    );

                    if (fallbackPricing) {
                      // Use the pricing from the initial grid data
                      console.log(`Found fallback pricing for capacity warning:`, fallbackPricing);
                      pricingId = fallbackPricing.price_point_id || 0;
                      showTimeSlotPricingId = fallbackPricing.id || 0;
                      assignedPricingId = fallbackPricing.pricing_id || 0;
                    } else {
                      // Last resort: use dummy IDs that backend might handle
                      console.warn(`No fallback pricing found, using dummy IDs`);
                      pricingId = 0;
                      showTimeSlotPricingId = 0;
                      assignedPricingId = 0;
                    }
                  } else {
                    throw new Error(`No pricing data found for show ${showItem.show?.id} at time ${showItem.time}`);
                  }
                }
              }
            } else {
              console.error('API call failed:', {
                status: response.status,
                statusText: response.statusText,
                url: `/api/staff/venues/${venueId}/shows?${queryParams}`
              });
            }

            // Fallback to props data if API call failed or pricing not found
            if (!assignedPricingId) {
              const fallbackPriceInfo = timeSlotPricing.find(
                (tp) =>
                  tp.show_id === showItem.show?.id &&
                  tp.time_slot.startsWith(showItem.time) &&
                  (tp.day_of_week === dayOfWeek || tp.day_of_week === null),
              );

              if (fallbackPriceInfo) {
                // If we have a capacity warning, use whatever pricing data we have
                if (showItem._useStoredPricing) {
                  // Use whatever we have, even if incomplete
                  pricingId = fallbackPriceInfo.price_point_id || showItem.pricePointId || null;
                  showTimeSlotPricingId = fallbackPriceInfo.id || showItem.showTimeSlotPricingId || null;
                  assignedPricingId = fallbackPriceInfo.pricing_id || showItem.pricingId || null;
                } else {
                  if (!fallbackPriceInfo.price_point_id || !fallbackPriceInfo.id) {
                    throw new Error(`Incomplete pricing data for show ${showItem.show?.id} at time ${showItem.time}`);
                  }

                  // Check if we have the required pricing_id
                  if (!fallbackPriceInfo.pricing_id) {
                    throw new Error(`No pricing_id found for show ${showItem.show?.id} at time ${showItem.time}. Please ensure pricing is properly configured.`);
                  }

                  pricingId = fallbackPriceInfo.price_point_id;
                  showTimeSlotPricingId = fallbackPriceInfo.id;
                  assignedPricingId = fallbackPriceInfo.pricing_id;
                }
              } else if (!showItem._useStoredPricing) {
                throw new Error(`No pricing information available for show ${showItem.show?.id} at time ${showItem.time}`);
              } else {
                // Use stored pricing from cart when we have a capacity warning
                pricingId = showItem.pricePointId || null;
                showTimeSlotPricingId = showItem.showTimeSlotPricingId || null;
                assignedPricingId = showItem.pricingId || null;
              }
            }
          }

          // Calculate price for this show
          // Use the bookingPrice from cart sidebar (user can modify this)
          const showPrice = bookingPrice;
          const showTotal =
            priceMode === "fixed"
              ? showPrice
              : showPrice * bookingCart.groupSize;

          // For the first show, include add-ons. For subsequent shows, only the show price
          const isFirstShow = showItem === bookingCart.shows[0];
          const addonsTotal = isFirstShow
            ? bookingCart.addOns.reduce((total, item) => {
                const price = item.addOn.is_per_person
                  ? (item.addOn.price || 0) * bookingCart.groupSize
                  : item.addOn.price || 0;
                return total + price * item.quantity;
              }, 0)
            : 0;
          const totalPrice = showTotal + addonsTotal;

          // Create ISO format timestamp for show session in local timezone
          // This preserves the exact date/time as selected by the user
          const showDate = new Date(showItem.date + "T00:00:00");
          const [hours, minutes] = showItem.time.split(":");
          showDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

          // Get timezone offset and create timestamp with local timezone
          const timezoneOffset = showDate.getTimezoneOffset();
          const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
          const offsetMinutes = Math.abs(timezoneOffset) % 60;
          const offsetSign = timezoneOffset <= 0 ? "+" : "-";
          const timezoneString = `${offsetSign}${offsetHours.toString().padStart(2, "0")}:${offsetMinutes.toString().padStart(2, "0")}`;

          const showSessionTimeslot = `${showItem.date}T${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:00${timezoneString}`;

          // Validate that we have all required pricing IDs (skip if we have a capacity warning)
          if ((!assignedPricingId || !showTimeSlotPricingId || !pricingId) && !showItem._useStoredPricing) {
            throw new Error(
              `Missing pricing information for show ${showItem.show?.show_infos?.[0]?.name || showItem.show?.id}. ` +
              `Please ensure pricing is configured for ${showItem.time} on ${showItem.date}.`
            );
          }

          // Prepare booking data based on booking type
          const baseBookingData = {
            // User Information
            name: customerInfo.name,
            email: customerInfo.email,
            phone: customerInfo.phone,

            // Booking Details
            showId: showItem.show?.id,
            venueId: parseInt(venueId),
            participants: bookingCart.groupSize,

            // Pricing Information
            assignedPricingId: assignedPricingId, // Use actual pricing ID from selected time slot
            showTimeSlotPricingId: showTimeSlotPricingId,
            pricePointId: pricingId,
            totalPrice: totalPrice,
            currency: venueCurrency,

            // Session Information
            showSessionTimeslot: showSessionTimeslot,

            // Additional Context
            date: showItem.date,
            time: showItem.time,
            dayOfWeek: dayOfWeek.toString(),
            bookingType: bookingCart.bookingType,
          };

          // Calculate custom duration if a custom end time is set and different from default
          let customDuration = undefined;
          if (showItem.customEndTime && showItem.customEndTime !== "") {
            const [startHour, startMin] = showItem.time.split(":").map(Number);
            const [endHour, endMin] = showItem.customEndTime.split(":").map(Number);
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;
            const calculatedDuration = endMinutes - startMinutes;

            // Only set custom duration if it's different from the show's default duration
            const defaultDuration = showItem.show?.duration_minutes;
            if (!defaultDuration) {
              console.error(`Warning: No duration for show ${showItem.show?.id}, skipping`);
              continue;
            }

            console.log("📏 Custom duration calculation:", {
              showId: showItem.show?.id,
              startTime: showItem.time,
              customEndTime: showItem.customEndTime,
              hasCustomEndTime: !!showItem.customEndTime,
              calculatedDuration,
              defaultDuration,
              willSetCustom: calculatedDuration > 0 && calculatedDuration !== defaultDuration,
              finalCustomDuration: calculatedDuration > 0 && calculatedDuration !== defaultDuration ? calculatedDuration : undefined
            });

            if (calculatedDuration > 0 && calculatedDuration !== defaultDuration) {
              customDuration = calculatedDuration;
            }
          }

          // Add to shows data array instead of making individual API calls
          showsData.push({
            showId: showItem.show?.id,
            studioId: showItem.studio?.id,
            assignedPricingId: assignedPricingId,
            showTimeSlotPricingId: showTimeSlotPricingId,
            pricePointId: pricingId,
            showSessionTimeslot: showSessionTimeslot,
            date: showItem.date,
            time: showItem.time,
            dayOfWeek: dayOfWeek.toString(),
            price: bookingPrice, // Use the price from cart sidebar (user can modify this)
            userRoleId: showItem.userRoleId, // Include the user role ID from the cart
            customDuration: customDuration, // Include custom duration if set
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          const showName = showItem.show?.show_infos?.[0]?.name || "show";

          // Check if the error is about participant capacity
          if (errorMessage.includes("requires") && errorMessage.includes("participants")) {
            // Show warning dialog for capacity issues
            setCapacityWarning({
              open: true,
              message: errorMessage,
              onConfirm: async () => {
                setCapacityWarning({ open: false, message: "", onConfirm: () => {} });
                // Continue with booking despite capacity warning
                // We'll need to modify the logic to allow proceeding despite the error
                toast.info("Proceeding with booking despite capacity warning");
                setIsSavingBooking(false);
              },
            });
            return;
          }

          // For other errors, show the error toast as before
          toast.error(`Failed to process ${showName}: ${errorMessage}`);
          return;
        }
      }

      // Function to make the actual API call (defined first so it's available for the callback)
      const proceedWithBookingAPI = async () => {
        // Now make a single API call with all shows
        try {
        let apiEndpoint = "";
        let requestData: any = {};
        let method = "POST";

        // Check if we're updating an existing booking first
        if (bookingId) {
          // For updates, we already set the endpoint and method above
          apiEndpoint = `/api/staff/venues/${venueId}/bookings/${bookingId}`;
          method = "PUT";

          // For updates, send ALL shows to support multiple show bookings

          requestData = {
            // User Information
            name: customerInfo.name,
            email: customerInfo.email,
            phone: customerInfo.phone,

            // Booking Details
            participants: bookingCart.groupSize,
            priceMode: priceMode, // Add price mode for updates too
            status_id: bookingStatus, // Pass the selected booking status
            // Show / communication language. old_lang lets the PUT route detect
            // a real change, log it, and notify operations (host language matters).
            lang: bookingCart.lang,
            old_lang: originalLang,

            // CRITICAL: Always send cart price for updates (not just when manually overridden)
            // This prevents the backend from reading potentially corrupted database values
            // The cart already has the correct price loaded from the database when editing
            // For per-person: send per-person price (pricing engine will multiply)
            // For fixed: send total fixed price
            // API will handle VAT extraction for private customers (see line 760-763 in route.ts)
            totalPrice: bookingPrice, // Send price as displayed (with VAT for private, without for corporate)
            currency: venueCurrency,

            // Price modifier information (always send to explicitly clear when null)
            priceModifierId: activePriceModifier
              ? activePriceModifier.id
              : null,
            priceModifierType: activePriceModifier
              ? activePriceModifier.modifier_type
              : null,
            priceModifierValue: activePriceModifier
              ? activePriceModifier.modifier_value
              : null,

            // Multiple shows support - send ALL shows
            shows: showsData.map((show) => {
              const showToSend = {
                showTimeSlotPricingId: show.showTimeSlotPricingId,
                showSessionTimeslot: show.showSessionTimeslot,
                studioId: show.studioId,
                assignedPricingId: show.assignedPricingId,
                pricePointId: show.pricePointId,
                customDuration: show.customDuration, // Include custom duration if set
              };
              console.log("📤 Show being sent to API:", {
                showTimeSlotPricingId: showToSend.showTimeSlotPricingId,
                customDuration: showToSend.customDuration,
                hasCustomDuration: showToSend.customDuration !== undefined,
                customDurationType: typeof showToSend.customDuration,
              });
              return showToSend;
            }),

            // Additional Context
            bookingType: bookingCart.bookingType,

            // Add-ons (use modified prices if available)
            addOns: bookingCart.addOns.map((item) => ({
              addOnId: item.addOn.id,
              quantity: item.quantity, // Use actual cart quantity (respects staff overrides)
              pricingId: item.addOn.pricing_id,
              price: addonPriceOverrides[item.addOn.id] ?? item.addOn.price,
              isPerPerson: item.addOn.is_per_person,
            })),

            // Notes
            notes: customerInfo.notes || "",

            // Change tracking - what user explicitly changed
            changedFields: changedFields,

            // Company information for corporate bookings
            ...(bookingCart.bookingType === "corporate" && {
              companyInfo: {
                companyName: companyInfo.companyName,
                orgNumber: companyInfo.orgNumber || undefined,
                vatNumber: companyInfo.vatNumber || undefined,
                address: companyInfo.address || undefined,
                contactPerson: companyInfo.contactPerson || undefined,
                department: companyInfo.department || undefined,
                invoiceReference: companyInfo.invoiceReference || undefined,
              },
              ...(partnerId != null && { partnerId }),
              ...(endCustomerName && { endCustomerName }),
            }),

            // Road-show pricing update: send the base fee + per-person rate and
            // the derived totals so the PUT route persists them (it handles these
            // when isRoadShowUpdate is set).
            ...(bookingCart.bookingType === "roadshow" &&
              (() => {
                const rsBase =
                  bookingCart.roadShowBasePrice ??
                  (Number((bookingCart.shows[0]?.show as any)?.base_price) || 0);
                const rsPer =
                  bookingCart.roadShowPerPersonPrice ??
                  (Number(bookingCart.shows[0]?.price) || 0);
                const rsSubtotal = rsBase + rsPer * bookingCart.groupSize;
                const rsVat = Math.round(rsSubtotal * 0.25 * 100) / 100;
                return {
                  isRoadShowUpdate: true,
                  skipPricingRecalculation: true,
                  show_base_price: rsBase,
                  per_person_mode_price_excluding_vat: rsPer,
                  base_amount_excl_vat: rsSubtotal,
                  vat_rate: 25,
                  vat_amount: rsVat,
                  total_price_incl_vat: rsSubtotal + rsVat,
                  kitId: bookingCart.kitId ?? null,
                };
              })()),
          };
        } else {
          // Use the staff create endpoint which supports both single and multiple shows
          apiEndpoint = `/api/staff/venues/${venueId}/bookings/create`;
          requestData = {
            // User Information
            name: customerInfo.name,
            email: customerInfo.email,
            phone: customerInfo.phone,

            // Booking Details
            participants: bookingCart.groupSize,
            bookingType: bookingCart.bookingType,
            priceMode: priceMode, // Add price mode
            isDraft: isDraft, // Pass isDraft flag to API
            status_id: bookingStatus, // Pass the selected booking status
            lang: bookingCart.lang, // Staff-selected show / communication language

            // Road-show equipment kit (persisted on studio_show_host_bookings)
            // and pricing (base fee + per-person rate, excl VAT).
            ...(bookingCart.bookingType === "roadshow" && {
              kitId: bookingCart.kitId ?? null,
              // Fall back to the show's base fee / per-person (slot) price so the
              // prefilled values are persisted even if staff didn't edit them.
              roadShowBasePrice:
                bookingCart.roadShowBasePrice ??
                (Number((bookingCart.shows[0]?.show as any)?.base_price) ||
                  null),
              roadShowPerPersonPrice:
                bookingCart.roadShowPerPersonPrice ??
                (Number(bookingCart.shows[0]?.price) || null),
            }),

            // Override price - only send if manually overridden
            // For per-person: send per-person price (pricing engine will multiply)
            // For fixed: send total fixed price
            ...(isPriceManuallyOverridden && {
              totalPrice: highestShowPrice, // Always send unit price (per-person or fixed)
            }),
            currency: venueCurrency,

            // Multiple shows data (API supports both single and multiple)
            shows: showsData,

            // Add-ons (use modified prices if available)
            addOns: bookingCart.addOns.map((item) => ({
              addOnId: item.addOn.id,
              quantity: item.quantity, // Use actual cart quantity (respects staff overrides)
              pricingId: item.addOn.pricing_id,
              price: addonPriceOverrides[item.addOn.id] ?? item.addOn.price,
              isPerPerson: item.addOn.is_per_person,
            })),

            // Notes
            notes: customerInfo.notes,

            // Company information for corporate bookings
            ...(bookingCart.bookingType === "corporate" && {
              companyInfo: {
                companyName: companyInfo.companyName,
                orgNumber: companyInfo.orgNumber || undefined,
                vatNumber: companyInfo.vatNumber || undefined,
                address: companyInfo.address || undefined,
                contactPerson: companyInfo.contactPerson || undefined,
                department: companyInfo.department || undefined,
                invoiceReference: companyInfo.invoiceReference || undefined,
              },
              ...(partnerId != null && { partnerId }),
              ...(endCustomerName && { endCustomerName }),
            }),
          };
        }

        // Debug: Log request data to see studios being sent
        console.log("🚀 Sending booking request with studios:", {
          endpoint: apiEndpoint,
          shows: requestData.shows,
          showsWithStudios: requestData.shows?.map((s: any) => ({
            showId: s.showId,
            studioId: s.studioId,
            hasStudioId: !!s.studioId
          }))
        });

        const response = await fetch(apiEndpoint, {
          method: method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        let result;
        try {
          const text = await response.text();
          result = text ? JSON.parse(text) : {};
        } catch (parseError) {
          console.error("❌ Failed to parse booking API response:", parseError);
          result = { success: false, error: "Invalid response from server" };
        }

        if (result.code === "GAP_CONFLICT" && result.conflictingBooking) {
          // The 15-minute rule between shows: the server refuses, no override.
          setGapWarningData(result.conflictingBooking);
          setGapWarningOpen(true);
          setIsSavingBooking(false);
        } else if (!response.ok || !result.success) {
          // Show specific error message
          const errorMessage =
            result.error ||
            (bookingId
              ? "Failed to update booking"
              : "Failed to create booking");
          toast.error(errorMessage);
        } else {
          // Check for warnings in successful response
          if (result.warnings && Array.isArray(result.warnings)) {
            result.warnings.forEach((warning: any) => {
              toast.warning(
                warning.message || "Warning occurred during update",
                {
                  duration: 8000,
                },
              );
            });
          }
          // Success! Handle both create and update scenarios
          const returnedBookingId = result.data?.bookingId || bookingId;

          // Check email status if available (only for new bookings)
          if (!bookingId && result.data?.emailStatus) {
            const emailStatus = result.data.emailStatus;

            if (isDraft) {
              // For draft bookings, just show success (no email sent)
              toast.success("Booking saved as draft! No email sent.");
            } else if (emailStatus.attempted) {
              // For confirmed bookings, check email status
              if (emailStatus.success) {
                toast.success("Booking confirmed! Email sent successfully.");
              } else {
                // Show warning if email failed
                toast.warning(
                  `Booking created successfully, but email failed: ${emailStatus.error || "Unknown error"}`,
                  {
                    duration: 8000,
                  },
                );
                console.error("Email error details:", emailStatus);
              }
            } else {
              // If email wasn't attempted for confirmed booking, show simple success
              toast.success("Booking created successfully!");
            }
          } else {
            // For updates or when no email status available
            if (isDraft) {
              toast.success("Booking saved as draft!");
            } else {
              const message = bookingId
                ? "Booking updated successfully"
                : "Booking created successfully!";
              toast.success(message);

              // Log email-related information for updates
              if (bookingId) {
                // Check if we can monitor the email status
                setTimeout(() => {}, 3000);
              }
            }
          }

          const showNames = bookingCart.shows
            .map((s) => s.show.show_infos?.[0]?.name || `Show ${s.show.id}`)
            .join(", ");

          if (bookingId) {
            // This was an update operation - only show one general success message
            // For updates, update the local state to reflect changes
            // Update group size to match what was saved
            setGroupSize(bookingCart.groupSize);

            // Reset change tracking flags after successful update
            // This ensures subsequent edits only track NEW changes
            setChangedFields({
              isParticipantChange: false,
              isAddOnChange: false,
              isPriceChange: false,
              isShowChange: false,
              isNotesChange: false,
            });

            // Close the cart sidebar
            setIsCartOpen(false);

            // Always redirect back to the booking details page after modifying
            const currentPath = window.location.pathname;
            const portal = currentPath.split("/")[1]; // booking, superadmin, etc.
            router.push(
              `/${portal}/venue/${venueId}/bookings/${bookingId}`,
            );

            // The success message was already shown above, no need for additional toasts
          } else {
            // This was a create operation
            toast.success(
              `Booking created successfully with ${bookingCart.shows.length} show${bookingCart.shows.length > 1 ? "s" : ""}: ${showNames}`,
            );

            // Reset the booking cart to clear the selection for new bookings
            setBookingCart({
              shows: [],
              groupSize: groupSize,
              customerType: null,
              addOns: [],
              notes: "",
              bookingType: CUSTOMER_TYPES.PRIVATE as "private",
              date: undefined,
              lang: "",
            });

            // Close the cart sidebar
            setIsCartOpen(false);

            // Redirect to the created booking details page
            if (returnedBookingId) {
              const currentPath = window.location.pathname;
              const portal = currentPath.split("/")[1]; // booking, superadmin, etc.
              router.push(
                `/${portal}/venue/${venueId}/bookings/${returnedBookingId}`,
              );
            }
          }

          // Trigger a refresh of the time slot grid
          setRefreshTrigger((prev) => prev + 1);
        }
      } catch (error) {
        console.error("❌ Booking API error:", error);
        toast.error(
          error instanceof Error
            ? error.message
            : bookingId
              ? "Failed to update booking"
              : "Failed to create booking",
        );
        setIsSavingBooking(false); // Reset on API error
      }
      }; // End of proceedWithBookingAPI function

      // Check if total participants exceed total studio capacity
      const capacityWarnings = [];

      // Calculate total studio capacity for all shows in cart
      const totalStudioCapacity = bookingCart.shows.reduce((sum, showItem) => {
        return sum + (showItem.studio?.capacity || 0);
      }, 0);

      const participantCount = bookingCart.groupSize;

      // Check if participants exceed total studio capacity
      if (totalStudioCapacity > 0 && participantCount > totalStudioCapacity) {
        const studioNames = bookingCart.shows.map(s =>
          s.studio?.name || "Unknown studio"
        ).join(" + ");

        capacityWarnings.push(
          `Total studio capacity (${studioNames}) is ${totalStudioCapacity} participants, but you have ${participantCount} participants. Some participants may not fit.`
        );
      }

      // Also check for any show-specific capacity warnings from the loop
      for (const showItem of bookingCart.shows) {
        if (showItem._capacityWarning) {
          capacityWarnings.push(showItem._capacityWarning);
          delete showItem._capacityWarning; // Clean up temporary property
        }
      }

      // If there are capacity warnings, show the dialog
      if (capacityWarnings.length > 0) {
        const warningMessage = capacityWarnings.join("\n");

        setCapacityWarning({
          open: true,
          message: warningMessage,
          onConfirm: async () => {
            setCapacityWarning({ open: false, message: "", onConfirm: () => {} });
            setIsSavingBooking(true); // Re-enable saving state when continuing
            // Continue with the booking by making the API call
            proceedWithBookingAPI();
          },
        });

        setIsSavingBooking(false);
        return; // Stop here and wait for user response
      }

      // If no warnings, proceed directly with the API call
      proceedWithBookingAPI();
    } catch (error) {
      console.error("❌ Unexpected booking error:", error);
      toast.error("An unexpected error occurred");
      setIsSavingBooking(false); // Only reset on error
    }
    // Note: We intentionally don't reset isSavingBooking on success
    // to prevent double-clicks during navigation
  };

  // Debugging function - you can call this in browser console
  // Only add to window if we're in the browser
  if (typeof window !== 'undefined') {
    (window as any).testEmailVariables = () => {
      const testTemplate =
        "Hi {{customer_name}}, we have updated your booking: {{latest_activity}}";
      const testVariables = {
        customer_name: "Test Customer",
        latest_activity: "Number of participants changed from 4 to 6",
      };

      let result = testTemplate;
      Object.entries(testVariables).forEach(([key, value]) => {
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
        result = result.replace(regex, value);
      });

      if (result.includes("{{")) {
        console.warn("⚠️ Some variables were not replaced!");
      } else {
      }
    };
  }

  const handleBack = () => {
    // If editing an existing booking, go to booking details page
    if (bookingId) {
      const bookingDetailsUrl = isSuperAdmin
        ? `/superadmin/venue/${venueId}/bookings/${bookingId}`
        : `/booking/venue/${venueId}/bookings/${bookingId}`;
      router.push(bookingDetailsUrl);
    } else {
      // Otherwise, go to bookings list
      const bookingsUrl = isSuperAdmin
        ? `/superadmin/venue/${venueId}/bookings`
        : `/booking/venue/${venueId}/bookings`;
      router.push(bookingsUrl);
    }
  };

  const selectedAddonsCount = bookingCart.addOns.length;
  const hasSelectedItems =
    bookingCart.shows.length > 0 || selectedAddonsCount > 0;

  return (
    <section className="min-h-screen bg-background">
      {/* Loading Bar at Top */}
      {isLoadingAvailability && (
        <div className="h-1 bg-gradient-to-r from-primary via-primary to-transparent animate-pulse sticky top-0 z-50" />
      )}

      {/* Top Navigation Bar */}
      <div className="bg-background border-b border-border/40 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold">Staff Booking System</h2>
                <span className="text-sm text-muted-foreground">
                  • {bookingId ? "Edit booking" : "New booking"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                className="relative"
                onClick={() => setIsCartOpen(true)}
              >
                <ShoppingCart className="h-4 w-4" />
                {hasSelectedItems && (
                  <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {bookingCart.shows.length + selectedAddonsCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-120px)]">
          {/* Left Column - Filters */}
          <div className="lg:col-span-1 overflow-y-auto pr-2 space-y-4">
            {/* Type Filter */}
            <CollapsibleSection
              title="Type"
              icon={<Building2 className="h-4 w-4" />}
              defaultExpanded={true}
            >
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={
                    bookingCart.bookingType === "corporate"
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  className="h-8 text-xs"
                  disabled={!!bookingId}
                  onClick={() =>
                    setBookingCart((prev) => ({
                      ...prev,
                      bookingType: "corporate",
                    }))
                  }
                >
                  <Building2 className="h-3 w-3 mr-1" />
                  Corp
                </Button>
                <Button
                  variant={
                    bookingCart.bookingType === "private"
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  className="h-8 text-xs"
                  disabled={!!bookingId}
                  onClick={() =>
                    setBookingCart((prev) => ({
                      ...prev,
                      bookingType: "private",
                    }))
                  }
                >
                  <Users className="h-3 w-3 mr-1" />
                  Private
                </Button>
                <Button
                  variant={
                    bookingCart.bookingType === "roadshow"
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  className="h-8 text-xs"
                  disabled={!!bookingId}
                  onClick={() =>
                    setBookingCart((prev) => ({
                      ...prev,
                      bookingType: "roadshow",
                    }))
                  }
                >
                  <Route className="h-3 w-3 mr-1" />
                  Road
                </Button>
              </div>
            </CollapsibleSection>

            {/* Size Filter */}
            <CollapsibleSection
              title="Size"
              icon={<Users className="h-4 w-4" />}
              defaultExpanded={!isAddonMode}
            >
              <div className="space-y-3">
                <Slider
                  min={venueDetails?.min_participant || 3}
                  max={venueDetails?.max_participant || 50}
                  step={1}
                  value={[groupSize]}
                  onValueChange={([value]) => {
                    // If editing an existing booking, mark this as a participant change
                    if (bookingId) {
                      setChangedFields((prev) => ({
                        ...prev,
                        isParticipantChange: true,
                      }));
                    }
                    setGroupSize(value);
                    setBookingCart((prev) => ({
                      ...prev,
                      groupSize: value,
                      // Update per-person add-on quantities to match new group size
                      // Note: This is automatic recalculation, NOT a user add-on change
                      addOns: prev.addOns.map((item) =>
                        item.addOn.is_per_person
                          ? { ...item, quantity: value }
                          : item,
                      ),
                    }));
                  }}
                  className="w-full"
                  disabled={isAddonMode}
                />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-center">
                    {groupSize} participants
                  </p>
                  <p className="text-xs text-center text-muted-foreground">
                    {isAddonMode
                      ? "Participant count locked when adding add-ons"
                      : "We'll find shows suitable for your group size"}
                  </p>
                </div>
              </div>
            </CollapsibleSection>

            {/* Time/Calendar Filter */}
            <CollapsibleSection
              title="Time / calendar"
              icon={<Clock className="h-4 w-4" />}
              defaultExpanded={!isAddonMode}
              expanded={expandedSections.timeSlots}
              onExpandedChange={(expanded) =>
                setExpandedSections(prev => ({ ...prev, timeSlots: expanded }))
              }
              dataSection="timeslots"
            >
              <div className="space-y-4">
                <div className="flex justify-center">
                  <BookingCalendar
                    mode="single"
                    selected={date}
                    disabled={isAddonMode ? true : { before: new Date() }}
                    endMonth={addDays(new Date(), 365)}
                    onDateClick={(newDate) => {
                      // Prevent date selection when in addon mode
                      if (isAddonMode) return;

                      // Ensure the date is at midnight local time
                      newDate.setHours(0, 0, 0, 0);
                      setDate(newDate);
                      setBookingCart((prev) => ({
                        ...prev,
                        date: (() => {
                          // Format date as YYYY-MM-DD without timezone issues
                          const year = newDate.getFullYear();
                          const month = String(
                            newDate.getMonth() + 1,
                          ).padStart(2, "0");
                          const day = String(newDate.getDate()).padStart(
                            2,
                            "0",
                          );
                          return `${year}-${month}-${day}`;
                        })(),
                      }));
                    }}
                    className={cn("rounded-md", isAddonMode && "opacity-50 pointer-events-none")}
                    getDateAvailability={(date: Date) => {
                      if (!date) return undefined;

                      // Use real availability data from the API
                      if (getRealDateAvailability) {
                        return getRealDateAvailability(date);
                      }

                      // Fallback logic for when API data is not available
                      const dayOfWeek = date.getDay();
                      if (dayOfWeek === 0 || dayOfWeek === 6)
                        return "partial"; // Weekend
                      return "available";
                    }}
                    startMonth={new Date()}
                    defaultMonth={date || new Date()}
                    isLoadingAvailability={isLoadingAvailability}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={cn("text-sm", isAddonMode && "text-muted-foreground")}>
                    {isAddonMode ? "Time range (locked)" : "Time range"}
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <TimeSelect
                      value={startTime}
                      onChange={setStartTime}
                      max={endTime}
                      interval={30}
                      placeholder="Start time"
                      className="text-sm"
                      disabled={isAddonMode}
                    />
                    <TimeSelect
                      value={endTime}
                      onChange={setEndTime}
                      min={startTime}
                      interval={30}
                      placeholder="End time"
                      className="text-sm"
                      disabled={isAddonMode}
                    />
                  </div>
                  {isAddonMode && (
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Date and time selection disabled when adding add-ons
                    </p>
                  )}
                </div>
              </div>
            </CollapsibleSection>
          </div>

          {/* Right Column - Shows and Add-ons */}
          <div className="lg:col-span-2 overflow-y-auto pl-2 space-y-6">
            {/* Time Slot Grid */}
            <CollapsibleSection
              title={`Time Slot Grid - ${date ? date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "Select a date"}`}
              icon={<Clock className="h-4 w-4" />}
              defaultExpanded={!isAddonMode}
            >
              {(() => {
                // Show loading state while fetching booking data
                if (bookingId && isLoadingBooking) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="h-12 w-12 mx-auto mb-3 opacity-50 animate-pulse" />
                      <p>Loading booking data...</p>
                    </div>
                  );
                }
                return date ? (
                  <TimeSlotGrid
                    key={`${date.toISOString()}-${bookingCart.bookingType}-${groupSize}-${startTime}-${endTime}-${refreshTrigger}`}
                    date={date}
                    shows={shows}
                    studios={studios}
                    timeSlotPricing={timeSlotPricing}
                    venueCurrency={venueCurrency}
                    venueId={venueId}
                    venueDefaultLanguage={venueDefaultLanguage}
                    onSlotSelect={handleTimeSlotSelect}
                    bookingType={bookingCart.bookingType}
                    customerTypes={customerTypes}
                    groupSize={groupSize}
                    startTime={startTime}
                    endTime={endTime}
                    bookingId={bookingId}
                    refreshTrigger={refreshTrigger}
                    setConfirmDialogOpen={setConfirmDialogOpen}
                    setPendingSlotSelection={setPendingSlotSelection}
                    bookingCart={bookingCart}
                    setBookingCart={setBookingCart}
                    setProceedWithSlotSelection={setProceedWithSlotSelectionFn}
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Loading time slots...</p>
                  </div>
                );
              })()}
            </CollapsibleSection>

            {/* Add-ons Section - Only show if there are add-ons available */}
            {(() => {
              // Filter add-ons based on selected booking type
              // customer_type_id: 1 = private, 2 = corporate
              // If customer_type_id is null, show for all types
              const customerTypeId =
                bookingCart.bookingType === "corporate" ? 2 : 1;
              const isRoadShow = bookingCart.bookingType === "roadshow";
              const filteredAddOns = addOns.filter((addOn) => {
                const onRoad = (addOn as any).is_on_road ?? false;
                // Road shows only offer road-show add-ons; on-site bookings only
                // offer regular add-ons (matching the customer type).
                if (isRoadShow) return onRoad === true;
                return (
                  onRoad !== true &&
                  (addOn.customer_type_id === null ||
                    addOn.customer_type_id === undefined ||
                    addOn.customer_type_id === customerTypeId)
                );
              });

              return filteredAddOns.length > 0 ? (
                <CollapsibleSection
                  title="Available Add-ons"
                  icon={<Plus className="h-4 w-4" />}
                  defaultExpanded={false}
                  expanded={expandedSections.addOns}
                  onExpandedChange={(expanded) =>
                    setExpandedSections(prev => ({ ...prev, addOns: expanded }))
                  }
                  dataSection="addons"
                >
                  <div className="grid gap-3">
                    {filteredAddOns.map((addOn) => {
                      const isSelected = bookingCart.addOns.some(
                        (item) => item.addOn.id === addOn.id,
                      );
                      return (
                        <Card
                          key={addOn.id}
                          className="cursor-pointer transition-all duration-200 hover:shadow-md"
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold text-base">
                                  {addOn.name}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {addOn.description}
                                </p>
                                <div className="flex items-center gap-3 mt-2 text-sm">
                                  <span className="flex items-center gap-1">
                                    <DollarSign className="h-3 w-3 text-muted-foreground" />

                                    {getCurrencySymbol(venueCurrency)}

                                    {(() => {
                                      // Use pricing service to calculate display price
                                      const pricing = PricingService.calculate({
                                        unitPrice: addOn.price ?? 0,
                                        vatPercentage:
                                          addOn.vat_percentage ?? 25,
                                        participants: 1, // For display, show unit price
                                        // Road shows are always priced excl VAT
                                        // (like corporate), so add-on prices in
                                        // the picker must show the net amount too.
                                        customerType:
                                          bookingCart.bookingType ===
                                            "corporate" ||
                                          bookingCart.bookingType === "roadshow"
                                            ? "corporate"
                                            : "private",
                                        priceType: addOn.is_per_person
                                          ? "per_person"
                                          : "fixed",
                                        addOns: [],
                                      });

                                      // displayAmount already accounts for customer type (incl VAT for private, excl VAT for corporate)
                                      return (
                                        pricing.displayAmount ?? 0
                                      ).toFixed(2);
                                    })()}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {addOn.is_per_person
                                      ? "Per person"
                                      : "One-time"}
                                  </span>
                                </div>
                              </div>

                              <Button
                                size="sm"
                                variant={isSelected ? "default" : "outline"}
                                onClick={() => handleAddOnToggle(addOn)}
                              >
                                {isSelected ? (
                                  <>
                                    <Check className="h-3 w-3 mr-1" />
                                    Added
                                  </>
                                ) : (
                                  <>
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add
                                  </>
                                )}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </CollapsibleSection>
              ) : null;
            })()}
          </div>
        </div>
      </div>

      {/* Cart Sidebar */}
      <CartSidebar
        open={isCartOpen}
        onOpenChange={setIsCartOpen}
        bookingCart={bookingCart}
        groupSize={bookingCart.groupSize}
        onGroupSizeChange={handleUpdateGroupSize}
        onRemoveShow={handleRemoveShow}
        onUpdateShowTime={handleUpdateShowTime}
        onUpdateShowStudio={handleUpdateShowStudio}
        onQuantityChange={handleQuantityChange}
        onRemoveAddon={handleRemoveAddon}
        onSaveBooking={handleSaveBooking}
        venueCurrency={venueCurrency}
        venueDefaultLanguage={venueDefaultLanguage}
        venueName={venueName}
        bookingId={bookingId}
        studios={studios}
        onUpdateAddonPrice={handleUpdateAddonPrice}
        isLoadingBookingData={isLoadingBookingData}
        isSavingBooking={isSavingBooking}
        activePriceModifier={activePriceModifier}
        originalPrice={originalPrice}
        bookingPrice={bookingPrice}
        onBookingPriceChange={setBookingPrice}
        onPriceManuallyChanged={setIsPriceManuallyOverridden}
        onPriceModeChange={setPriceMode}
        onDateChange={handleDateChange}
        onLanguageChange={(lang) =>
          setBookingCart((prev) => ({ ...prev, lang }))
        }
        onKitChange={(kitId) =>
          setBookingCart((prev) => ({ ...prev, kitId }))
        }
        onRoadShowBasePriceChange={(roadShowBasePrice) =>
          setBookingCart((prev) => ({ ...prev, roadShowBasePrice }))
        }
        onRoadShowPerPersonPriceChange={(roadShowPerPersonPrice) =>
          setBookingCart((prev) => ({ ...prev, roadShowPerPersonPrice }))
        }
        availableLanguages={availableLanguages}
        existingBookingPricing={existingBookingPricing}
      />

      {/* Confirmation Dialog for Non-Recommended Slots */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Warning
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Are you sure? This is not a recommended slot time.
            </div>

            <Alert className="border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950">
              <AlertDescription className="text-black dark:text-white">
                <div>
                  You selected:{" "}
                  <span className="font-bold">
                    {pendingSlotSelection?.time}
                  </span>{" "}
                  for{" "}
                  <span className="font-bold">
                    {pendingSlotSelection?.show.show_infos?.[0]?.name ||
                      `Show ${pendingSlotSelection?.show.id}`}
                  </span>
                </div>
              </AlertDescription>
            </Alert>
          </div>

          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel
              onClick={() => {
                setConfirmDialogOpen(false);
                setPendingSlotSelection(null);
                setProceedWithSlotSelectionFn(null);
              }}
              className="mr-2"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (proceedWithSlotSelectionFn) {
                  proceedWithSlotSelectionFn();
                }
                setConfirmDialogOpen(false);
                setPendingSlotSelection(null);
                setProceedWithSlotSelectionFn(null);
              }}
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              Continue Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Road Show Modal */}
      <NewRoadShowModal
        isOpen={isRoadShowModalOpen}
        onClose={() => setIsRoadShowModalOpen(false)}
        venueId={venueId}
        portalType={isSuperAdmin ? "superadmin" : "booking"}
        venueCurrency={venueCurrency}
      />

      {/* Booking Gap Warning Modal */}
      <BookingGapWarningModal
        isOpen={gapWarningOpen}
        conflictingShow={gapWarningData || undefined}
        onCancel={handleGapWarningCancel}
      />

      <CapacityWarningDialog
        open={capacityWarning.open}
        onOpenChange={(open) => setCapacityWarning({ ...capacityWarning, open })}
        warningMessage={capacityWarning.message}
        onConfirm={capacityWarning.onConfirm}
        onCancel={() => {
          setCapacityWarning({ open: false, message: "", onConfirm: () => {} });
          setIsSavingBooking(false);
        }}
      />
    </section>
  );
}
